import { useEffect, useRef, useState } from 'react';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

export interface MapPoint {
  sn: string;
  name?: string | null;
  lat: number;
  lng: number;
}

/** Reject null, (0,0) placeholder, and out-of-range coordinates. */
export function isValidLatLng(lat: number | null, lng: number | null): lat is number {
  return (
    lat != null &&
    lng != null &&
    !(lat === 0 && lng === 0) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

// Load the Google Maps JS API once (shared promise across all map instances).
let loaderPromise: Promise<void> | null = null;
function loadGoogleMaps(): Promise<void> {
  const w = window as unknown as { google?: { maps?: unknown } };
  if (w.google?.maps) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise<void>((resolve, reject) => {
    if (!MAPS_KEY) {
      reject(new Error('VITE_GOOGLE_MAPS_API_KEY non configure'));
      return;
    }
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&v=weekly&language=fr&region=FR`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('echec de chargement de Google Maps'));
    document.head.appendChild(s);
  });
  return loaderPromise;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

export function DeviceMap({ points, height = 420 }: Readonly<{ points: MapPoint[]; height?: number }>) {
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  // Recreate the map only when the set of points actually changes.
  const key = points.map((p) => `${p.sn}:${p.lat}:${p.lng}`).join('|');

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => {
        if (cancelled || !ref.current) return;
        const g = (window as unknown as { google: any }).google;
        const center = points.length
          ? { lat: points[0].lat, lng: points[0].lng }
          : { lat: 46.6, lng: 2.4 }; // France
        const map = new g.maps.Map(ref.current, {
          center,
          zoom: points.length ? 11 : 5,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        const bounds = new g.maps.LatLngBounds();
        points.forEach((p) => {
          const marker = new g.maps.Marker({
            position: { lat: p.lat, lng: p.lng },
            map,
            title: p.name || p.sn,
          });
          const info = new g.maps.InfoWindow({
            content: `<strong>${escapeHtml(p.name || p.sn)}</strong><br/>${escapeHtml(p.sn)}`,
          });
          marker.addListener('click', () => info.open({ anchor: marker, map }));
          bounds.extend(marker.getPosition());
        });
        if (points.length > 1) map.fitBounds(bounds);
        // Safety: recompute size in case the container was toggled in.
        setTimeout(() => {
          if (cancelled) return;
          g.maps.event.trigger(map, 'resize');
          if (points.length <= 1) map.setCenter(center);
        }, 250);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, height]);

  if (error) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-red-400 text-sm bg-inset rounded"
      >
        Carte indisponible : {error}
      </div>
    );
  }
  return <div ref={ref} style={{ height, width: '100%' }} className="rounded" />;
}
